import { useEffect, useRef, useState } from "react";
import { HardDrive, Loader2, Check, ChevronDown, AlertCircle } from "lucide-react";
import {
  getStorage,
  connectStorage,
  disconnectStorage,
  type StorageStatus,
  type ProvisionStep,
} from "../../features/vault/vaultApi";

const REGIONS = ["us-east-1", "us-west-2", "eu-west-1", "eu-central-1", "ap-south-1", "ap-southeast-1", "ap-southeast-2"];

// The provisioning steps, in order, with the label shown in the live tracker.
const STEPS: { key: ProvisionStep; label: string }[] = [
  { key: "validating", label: "Validating your AWS keys" },
  { key: "creating-bucket", label: "Creating your private S3 bucket" },
  { key: "securing-bucket", label: "Locking down access + encryption" },
  { key: "configuring-cors", label: "Configuring upload permissions" },
  { key: "creating-cdn", label: "Creating your CloudFront CDN" },
  { key: "finalizing", label: "Linking the CDN to your bucket" },
];

function stepIndex(step?: ProvisionStep): number {
  if (!step) return -1;
  if (step === "ready") return STEPS.length;
  const i = STEPS.findIndex((s) => s.key === step);
  return i;
}

/** Bring-your-own AWS: enter keys → we auto-create a private bucket + CloudFront
 *  CDN in their account and route all uploads/serving through it. */
export default function StorageSettings() {
  const [status, setStatus] = useState<StorageStatus | null>(null);
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [region, setRegion] = useState("us-east-1");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPolicy, setShowPolicy] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const provisioning = status?.provisioning;
  const isProvisioning = Boolean(provisioning && provisioning.step !== "ready" && provisioning.step !== "error");

  // Poll live progress while a provision is running.
  useEffect(() => {
    if (!isProvisioning) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => {
      getStorage().then(setStatus).catch(() => undefined);
    }, 2500);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isProvisioning]);

  // Keep polling (slower) while the CDN finishes deploying, so the badge updates.
  useEffect(() => {
    if (status?.enabled && provisioning?.cdnStatus === "deploying") {
      const t = setInterval(() => getStorage().then(setStatus).catch(() => undefined), 15000);
      return () => clearInterval(t);
    }
  }, [status?.enabled, provisioning?.cdnStatus]);

  useEffect(() => {
    getStorage().then(setStatus).catch(() => undefined);
  }, []);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      const s = await connectStorage(keyId.trim(), secret.trim(), region);
      setStatus(s);
      setKeyId("");
      setSecret("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not connect");
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!confirm("Disconnect your storage and use Jewelitt storage again? Existing models stay in your bucket.")) return;
    setBusy(true);
    try {
      await disconnectStorage();
      setStatus({ enabled: false, region: null, bucket: null, cloudfrontDomain: null, accessKeyId: null, provisioning: null });
    } finally {
      setBusy(false);
    }
  };

  const curIdx = stepIndex(provisioning?.step);

  return (
    <div className="surface-card mt-6 p-6">
      <div className="flex items-center gap-2">
        <HardDrive size={16} className="text-gold-500" />
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-ink-400">Your storage (BYO AWS)</h2>
      </div>

      {/* ───── Provisioning in progress: live step tracker ───── */}
      {isProvisioning ? (
        <div className="mt-4">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-ink-900">
            <Loader2 size={15} className="animate-spin text-gold-500" />
            Setting up your storage…
          </div>
          <p className="mt-1 text-[12.5px] text-ink-500">
            This usually takes under a minute. Your CloudFront CDN keeps deploying in the background (~15 min) — you can
            start uploading as soon as this finishes.
          </p>
          <ol className="mt-4 space-y-2.5">
            {STEPS.map((s, i) => {
              const done = i < curIdx;
              const active = i === curIdx;
              return (
                <li key={s.key} className="flex items-center gap-2.5 text-[13px]">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                    {done ? (
                      <Check size={15} className="text-emerald" />
                    ) : active ? (
                      <Loader2 size={14} className="animate-spin text-gold-500" />
                    ) : (
                      <span className="h-2 w-2 rounded-full bg-ink-200" />
                    )}
                  </span>
                  <span className={done ? "text-ink-500" : active ? "font-medium text-ink-900" : "text-ink-300"}>
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      ) : status?.enabled ? (
        /* ───── Connected ───── */
        <div className="mt-4">
          <div className="flex items-center gap-2 rounded-xl border border-emerald/30 bg-emerald/5 px-4 py-3">
            <Check size={16} className="text-emerald" />
            <div className="text-[13px]">
              <p className="font-semibold text-ink-900">Connected to your AWS</p>
              <p className="text-ink-500">
                Bucket <span className="font-mono">{status.bucket}</span> · {status.region} · CDN{" "}
                <span className="font-mono">{status.cloudfrontDomain}</span>
              </p>
            </div>
          </div>
          {provisioning?.cdnStatus === "deploying" ? (
            <p className="mt-2 flex items-center gap-1.5 text-[12px] text-ink-500">
              <Loader2 size={12} className="animate-spin text-gold-500" />
              CDN still deploying (~15 min) — uploads + delivery already work via your bucket.
            </p>
          ) : (
            <p className="mt-2 text-[12px] text-ink-400">
              All uploads, GLB delivery, and thumbnails use your bucket + CDN.
            </p>
          )}
          <button
            onClick={disconnect}
            disabled={busy}
            className="mt-3 rounded-lg border border-ink-200 px-3 py-1.5 text-[12.5px] font-medium text-ink-600 hover:border-rose hover:text-rose disabled:opacity-50"
          >
            Disconnect
          </button>
        </div>
      ) : (
        /* ───── Not connected (or last attempt errored): the form ───── */
        <>
          <p className="mt-2 text-[13px] leading-relaxed text-ink-500">
            Store and serve your 3D files from your own AWS account. Paste keys and we'll automatically
            create a private S3 bucket + CloudFront CDN for you — no console setup.
          </p>

          {provisioning?.step === "error" && provisioning.error && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose/30 bg-rose/5 px-3 py-2.5 text-[12.5px] text-rose">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{provisioning.error}</span>
            </div>
          )}

          <div className="mt-4 space-y-3">
            <input
              value={keyId}
              onChange={(e) => setKeyId(e.target.value)}
              placeholder="AWS Access Key ID"
              className="w-full rounded-lg border border-ink-200 px-3 py-2 font-mono text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
            />
            <input
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              type="password"
              placeholder="AWS Secret Access Key"
              className="w-full rounded-lg border border-ink-200 px-3 py-2 font-mono text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
            />
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-[13px] text-ink-800 focus:border-gold-400 focus:outline-none"
            >
              {REGIONS.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
            {error && <p className="text-[12.5px] text-rose">{error}</p>}
            <button
              onClick={connect}
              disabled={busy || !keyId.trim() || !secret.trim()}
              className="btn-press flex items-center justify-center gap-2 rounded-lg bg-ink-900 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-ink-800 disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              {busy ? "Starting…" : "Connect & auto-provision"}
            </button>
          </div>

          <button
            onClick={() => setShowPolicy((v) => !v)}
            className="mt-4 flex items-center gap-1 text-[12px] font-medium text-ink-500 hover:text-ink-800"
          >
            <ChevronDown size={13} className={showPolicy ? "rotate-180 transition-transform" : "transition-transform"} />
            Required AWS permissions
          </button>
          {showPolicy && (
            <pre className="mt-2 overflow-x-auto rounded-lg bg-ink-900 p-3 font-mono text-[11px] leading-relaxed text-ink-100">
{`{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": [
      "sts:GetCallerIdentity",
      "s3:CreateBucket", "s3:PutBucketPolicy", "s3:PutBucketCORS",
      "s3:PutBucketPublicAccessBlock", "s3:PutEncryptionConfiguration",
      "s3:PutObject", "s3:GetObject", "s3:DeleteObject", "s3:ListBucket",
      "cloudfront:CreateOriginAccessControl", "cloudfront:CreateDistribution",
      "cloudfront:GetDistribution"
    ],
    "Resource": "*"
  }]
}`}
            </pre>
          )}
        </>
      )}
    </div>
  );
}
